import { getAssetUrl } from '@lighthouse/assets'
import type {
    CalendarConfig,
    Field,
    FieldADTValue,
    FilterFormType,
    NumberValue,
    RecordLikeProtocol,
    VariableADTvalue
} from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import { add, endOfDay, isValid, lightFormat, parse, startOfDay } from 'date-fns'
import { max, reduce } from 'rambda'
import React, { useCallback, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { TEXT_FONT_NORMAL } from '../../../../constants'
import { useApplicationContext } from '../../../../contexts'
import { useTimezoneOffset } from '../../../../hooks/useTimezoneOffset'
import {
    extractLunarDate,
    fieldConvertValue,
    fromShanghaiWall,
    getBorderStyle,
    getCondition,
    isBeijingTimeZone,
    isDateValue,
    recTakeFilterVariable,
    resolveFilter,
    toShanghaiWall
} from '../../../../utils'
import { applyFillPickerValue4CssColorValue, useFillPickerContext } from '../../../FillPicker'
import { useFlowLayoutContext } from '../../../LayoutEngine/Context'
import { defaultFormat } from '../../constant'
import type { FieldBaseProps } from '../../types'
import { Calendar } from './Calendar'

interface CalendarFieldProps extends FieldBaseProps {
    value?: NumberValue
    calendarField?: Field
    calendarConfig?: CalendarConfig
}

const SCxContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    /* padding: 0 8px; */
    display: flex;
    align-items: center;
    font-size: var(--font-size-normal);
`

const SCxWrapper = styled.div`
    position: absolute;
    inset: 0;
    /* padding: 0 8px; */
    display: flex;
    align-items: center;
    font-size: var(--font-size-normal);
`

const CalendarField: React.FunctionComponent<CalendarFieldProps> = props => {
    const { readOnly, onChange, value, config, calendarField, calendarConfig, onFetchCalendarOptions, dataSourceList } = props
    const { breakPoint } = config
    const { calendar, calendarDate, calendarHeader, calendarWeek } = breakPoint ?? {}
    const fieldType = calendarField?.type || 'date'
    const { calendar: configCalendar } = calendarConfig || {}
    const {
        mode,
        dateFormat: calendarDateFormat,
        customDays,
        range,
        enabled,
        rangeDsId,
        rangeFieldId,
        rangeFilter,
        describeDsId,
        filter: describeFilter,
        describeFieldId,
        describeTextFieldId,
        timeZone
    } = configCalendar || {}
    const useBeijing = isBeijingTimeZone(timeZone)
    const tzOffset = useTimezoneOffset()

    const { palettes } = useFillPickerContext()
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()

    const describeDataSource = useMemo(() => dataSourceList?.find(item => item.id === describeDsId), [dataSourceList, describeDsId])

    const withLunarFormat = useMemo(() => {
        if (calendarField?.type === 'date') {
            return calendarField.date.format ?? defaultFormat
        }

        if (calendarField?.type === 'text') {
            return calendarDateFormat ?? 'yyyy/MM/dd'
        }

        return defaultFormat
    }, [calendarDateFormat, calendarField])

    const [isLunar, finalFormat] = useMemo(() => {
        const includesLunar = withLunarFormat.includes('lunar-')
        const normalFormat = withLunarFormat
            .split(' ')
            .map(item => item.replace('lunar-', ''))
            .join(' ')
        return [includesLunar, normalFormat]
    }, [withLunarFormat])

    const usedValue = useMemo(() => {
        if (!value) {
            return
        }
        if (fieldType === 'text') {
            // 农历日期格式
            if (isLunar) {
                return extractLunarDate(value.toString())?.getTime() || ''
            }

            const date = parse(value.toString(), finalFormat, new Date())
            if (isValid(date)) {
                return date.getTime()
            }

            return ''
        }
        return value
    }, [fieldType, finalFormat, isLunar, value])

    const valueDate = useMemo(() => {
        const currentValue = usedValue ? new Date(usedValue) : undefined
        if (!currentValue || !isValid(currentValue)) {
            return
        }
        if (useBeijing && fieldType !== 'text') {
            return toShanghaiWall(currentValue.getTime())
        }
        return currentValue
    }, [fieldType, tzOffset, useBeijing, usedValue])

    const todayWall = useCallback(() => {
        return startOfDay(useBeijing ? toShanghaiWall(Date.now()) : new Date())
    }, [tzOffset, useBeijing])

    const [minDate, maxDate] = useMemo(() => {
        switch (range) {
            case 'AFTER_TODAY': {
                return [todayWall(), undefined]
            }
            case 'BEFORE_TODAY': {
                return [undefined, endOfDay(todayWall())]
            }
            case 'AFTER_CUSTOM': {
                return [
                    customDays
                        ? add(todayWall(), {
                              days: max(customDays, 0)
                          })
                        : undefined,
                    undefined
                ]
            }
            default: {
                return []
            }
        }
    }, [customDays, range, todayWall])

    const { useParseVariableWithSubscription } = useFlowLayoutContext()

    const rangeFilterVariables = useMemo(() => rangeFilter && recTakeFilterVariable(rangeFilter?.expression), [rangeFilter])
    const parseVariable2Field4RangeFilter = useParseVariableWithSubscription(rangeFilterVariables)
    const resolvedRangeFilter = useMemo(() => {
        if (!rangeFilter) {
            return
        }
        return resolveFilter({
            filter: rangeFilter,
            extraParams: {
                dataSourceList,
                parseVariable2Field: parseVariable2Field4RangeFilter
            }
        })
    }, [dataSourceList, rangeFilter, parseVariable2Field4RangeFilter])

    // 请求可用日期
    const fetchEnableSelectedDateList = useCallback(
        async (firstTime: Date, lastTime: Date) => {
            if (!rangeDsId || !rangeFieldId || range !== 'DATASOURCE_CUSTOM') {
                return
            }
            const startTimeCondition = getCondition(
                {
                    type: VariableType.FIELD_ID,
                    fieldIdVariable: {
                        fieldId: rangeFieldId
                    }
                },
                '>=',
                [
                    {
                        type: VariableType.VALUE,
                        valueVariable: {
                            value: useBeijing ? fromShanghaiWall(firstTime) : firstTime.getTime()
                        }
                    } as VariableADTvalue
                ]
            )
            const endTimeCondition = getCondition(
                {
                    type: VariableType.FIELD_ID,
                    fieldIdVariable: {
                        fieldId: rangeFieldId
                    }
                },
                '<=',
                [
                    {
                        type: VariableType.VALUE,
                        valueVariable: {
                            value: useBeijing ? fromShanghaiWall(lastTime) : lastTime.getTime()
                        }
                    } as VariableADTvalue
                ]
            )
            const initFilter: FilterFormType = {
                expression: {
                    conditions: [
                        {
                            where: 'AND',
                            conditions: [startTimeCondition, endTimeCondition]
                        },
                        resolvedRangeFilter?.expression
                    ],
                    where: 'AND'
                }
            }

            const records = await onFetchCalendarOptions?.({
                dsId: rangeDsId,
                filter: initFilter
            })
            if (records) {
                return reduce<RecordLikeProtocol, number[]>(
                    (p, c) => {
                        const timestamp = c?.content?.[rangeFieldId]?.value
                        if (timestamp && isDateValue(timestamp)) {
                            const daySource = useBeijing ? toShanghaiWall(Number(timestamp)) : Number(timestamp)
                            const dayTimestamp = startOfDay(daySource).getTime()
                            return [...p, dayTimestamp]
                        }
                        return p
                    },
                    [],
                    records
                )
            }
        },
        [onFetchCalendarOptions, range, rangeDsId, rangeFieldId, resolvedRangeFilter, useBeijing]
    )

    const describeFilterVariables = useMemo(() => describeFilter && recTakeFilterVariable(describeFilter?.expression), [describeFilter])
    const parseVariable2Field4DescribeFilter = useParseVariableWithSubscription(describeFilterVariables)
    const resolvedDescribeFilter = useMemo(() => {
        if (!describeFilter) {
            return
        }
        return resolveFilter({
            filter: describeFilter,
            extraParams: {
                dataSourceList,
                parseVariable2Field: parseVariable2Field4DescribeFilter
            }
        })
    }, [dataSourceList, describeFilter, parseVariable2Field4DescribeFilter])

    //  获取对应日期的描述
    const fetchDateDescribeMap = useCallback(
        async (firstTime: Date, lastTime: Date) => {
            if (!describeDsId || !describeFieldId || !describeTextFieldId || !enabled) {
                return
            }
            const startTimeCondition = getCondition(
                {
                    type: VariableType.FIELD_ID,
                    fieldIdVariable: {
                        fieldId: describeFieldId
                    }
                },
                '>=',
                [
                    {
                        type: VariableType.VALUE,
                        valueVariable: {
                            value: useBeijing ? fromShanghaiWall(firstTime) : firstTime.getTime()
                        }
                    } as VariableADTvalue
                ]
            )
            const endTimeCondition = getCondition(
                {
                    type: VariableType.FIELD_ID,
                    fieldIdVariable: {
                        fieldId: describeFieldId
                    }
                },
                '<=',
                [
                    {
                        type: VariableType.VALUE,
                        valueVariable: {
                            value: useBeijing ? fromShanghaiWall(lastTime) : lastTime.getTime()
                        }
                    } as VariableADTvalue
                ]
            )
            const initFilter: FilterFormType = {
                expression: {
                    conditions: [
                        {
                            where: 'AND',
                            conditions: [startTimeCondition, endTimeCondition]
                        },
                        resolvedDescribeFilter?.expression
                    ],
                    where: 'AND'
                }
            }

            const records = await onFetchCalendarOptions?.({
                dsId: describeDsId,
                filter: initFilter
            })
            if (records) {
                return reduce<RecordLikeProtocol, Record<number, string>>(
                    (p, record) => {
                        const timestamp = record?.content?.[describeFieldId]?.value
                        if (isDateValue(timestamp)) {
                            const text = record.content?.[describeTextFieldId]?.value
                            if (text) {
                                const field = describeDataSource?.schema?.[describeTextFieldId]
                                const daySource = useBeijing ? toShanghaiWall(Number(timestamp)) : Number(timestamp)
                                const dayTimestamp = startOfDay(daySource).getTime()
                                p[dayTimestamp] = fieldConvertValue({ value: text, ...field } as FieldADTValue, {
                                    personOptions,
                                    roleOptions,
                                    departmentOptions
                                })
                            }
                        }
                        return p
                    },
                    {},
                    records
                )
            }
        },
        [
            describeDsId,
            describeFieldId,
            describeTextFieldId,
            enabled,
            resolvedDescribeFilter?.expression,
            onFetchCalendarOptions,
            describeDataSource?.schema,
            personOptions,
            roleOptions,
            departmentOptions,
            useBeijing
        ]
    )

    const handleChange = useCallback(
        (v?: Date) => {
            if (fieldType === 'text') {
                onChange?.({ type: 'text', value: v ? lightFormat(v, finalFormat) : '' })
                return
            }
            if (!v) {
                onChange?.({ type: 'date', value: '' })
                return
            }
            onChange?.({ type: 'date', value: useBeijing ? fromShanghaiWall(v) : v.valueOf() })
        },
        [fieldType, finalFormat, onChange, useBeijing]
    )

    useEffect(() => {
        const ffList = [calendarDate?.fontFamily, calendarHeader?.fontFamily, calendarWeek?.fontFamily].filter(Boolean) as string[]
        document.fonts.ready
            .then(async ffs => {
                const fontFaceList = [...ffs.values()]
                async function loadFont(ff: string) {
                    const isLoaded = fontFaceList.some(item => item.family === ff)
                    if (!isLoaded) {
                        const fontUrl = `url(${getAssetUrl('font', `${ff}.woff2`)})`
                        const newFont = new FontFace(ff, fontUrl)
                        newFont._snapdomSrc = `${getAssetUrl('font', `${ff}.woff2`)}`
                        await newFont.load()
                        ffs.add(newFont)
                    }
                }
                await Promise.all(ffList.map(loadFont))
            })

            .catch(console.warn)
    }, [calendarDate?.fontFamily, calendarHeader?.fontFamily, calendarWeek?.fontFamily])

    return (
        <SCxContainer>
            <SCxWrapper>
                <Calendar
                    style={{
                        ...(calendar?.border ? getBorderStyle(calendar?.border, palettes) : {}),
                        backgroundColor: applyFillPickerValue4CssColorValue(calendar?.backgroundColor.color, palettes)
                    }}
                    styles={{
                        header: {
                            fontFamily: calendarHeader?.fontFamily === TEXT_FONT_NORMAL ? undefined : calendarHeader?.fontFamily,
                            fontColor: applyFillPickerValue4CssColorValue(calendarHeader?.fontColor.color, palettes),
                            iconColor: applyFillPickerValue4CssColorValue(calendarHeader?.iconColor.color, palettes)
                        },
                        headerWeek: {
                            fontFamily: calendarWeek?.fontFamily === TEXT_FONT_NORMAL ? undefined : calendarWeek?.fontFamily,
                            fontColor: applyFillPickerValue4CssColorValue(calendarWeek?.fontColor.color, palettes)
                        },
                        day: {
                            fontFamily: calendarDate?.fontFamily === TEXT_FONT_NORMAL ? undefined : calendarDate?.fontFamily,
                            hoverFontColor: applyFillPickerValue4CssColorValue(calendarDate?.hoverFontColor.color, palettes),
                            selectedFontColor: applyFillPickerValue4CssColorValue(calendarDate?.selectedFontColor.color, palettes),
                            currentMonthFontColor: applyFillPickerValue4CssColorValue(calendarDate?.currentMonthFontColor.color, palettes),
                            hoverBackgroundColor: applyFillPickerValue4CssColorValue(calendarDate?.hoverBackgroundColor.color, palettes),
                            selectedBackgroundColor: applyFillPickerValue4CssColorValue(
                                calendarDate?.selectedBackgroundColor.color,
                                palettes
                            ),
                            notCurrentMonthFontColor: applyFillPickerValue4CssColorValue(
                                calendarDate?.notCurrentMonthFontColor.color,
                                palettes
                            ),
                            describeFontColor: applyFillPickerValue4CssColorValue(calendar?.describeFontColor.color, palettes)
                        }
                    }}
                    mode={mode}
                    timeZone={timeZone}
                    weekShowCount={calendar?.weekShowCount}
                    minDate={minDate}
                    maxDate={maxDate}
                    onFetchDateDescribeMap={fetchDateDescribeMap}
                    onFetchEnableSelectedDateList={fetchEnableSelectedDateList}
                    disabled={readOnly}
                    date={valueDate}
                    onChange={handleChange}
                />
            </SCxWrapper>
        </SCxContainer>
    )
}

export default CalendarField

// 为以下每个属性生成文档
